import { useMemo } from 'react'
import {
  BlockWithVariants,
  isBlockWithVariants,
  isInstanceBlock,
  isListBlock,
  isVariantSetBlock,
  withUniqueApiNames,
  type Block,
} from '@basehub/data/replicache/index'
import { AtLeast, TreeManager } from '@basehub/utils/tree/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useDeepCompareMemo } from 'use-deep-compare'

const getBlockAcceptedVariantSets = (
  block: AtLeast<Block, 'type' | 'constraints' | 'typeOptions'>,
  getNode: TreeManager<Block, NonNullable<unknown>>['getNode']
): string[] => {
  if (isBlockWithVariants(block as Block)) {
    return (block as BlockWithVariants).constraints.acceptedVariantSets ?? []
  }
  if (isInstanceBlock(block) || isListBlock(block)) {
    const target = getNode(block.typeOptions.targetBlockId)?.value

    if (!target) {
      // don't throw, be permissive, so that this doesn't break the Diff ui
      return []
    }

    if (isBlockWithVariants(target)) {
      return target.constraints.acceptedVariantSets ?? []
    }
  }

  return []
}

/**
 * Returns the selected variants that are accepted by the parent block and the block itself.
 *
 * Block is optional because if we want to render all possible variants (for example in the properties panel)
 * we don't need to validate the block constraints
 */
export const useSelectedVariants = ({
  parentBlock,
  block,
  ignoreDefault = true,
}: {
  parentBlock: Block | undefined
  block?: Block
  ignoreDefault?: boolean
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const selectedVariants = useUserPreferences((s) => s.selectedVariants)

  const filtered = useDeepCompareMemo(() => {
    if (!parentBlock?.type || !getNode) return []
    const parentAcceptedSets = getBlockAcceptedVariantSets(
      {
        type: parentBlock.type,
        constraints: parentBlock.constraints,
        typeOptions: parentBlock.typeOptions,
      } as AtLeast<Block, 'type' | 'constraints' | 'typeOptions'>,
      getNode
    )
    if (parentAcceptedSets.length) {
      return selectedVariants.filter((v) => {
        const node = getNode(v.setId)?.value
        if (!node || !isVariantSetBlock(node)) return false
        const defaultVariant = node?.value.variants.find((v) => v.isDefault)
        if (!defaultVariant) return false
        const isAcceptedInBlock =
          block?.type &&
          getBlockAcceptedVariantSets(
            {
              type: block.type,
              constraints: block.constraints,
              typeOptions: block.typeOptions,
            } as AtLeast<Block, 'type' | 'constraints' | 'typeOptions'>,
            getNode
          ).includes(v.setId)
        return (
          parentAcceptedSets.some((setId) => setId === v.setId) &&
          (!ignoreDefault || defaultVariant.id !== v.variantId) &&
          (!block?.type || isAcceptedInBlock)
        )
      })
    }
    return []
  }, [
    block?.constraints,
    block?.type,
    block?.typeOptions,
    getNode,
    ignoreDefault,
    parentBlock?.constraints,
    parentBlock?.type,
    parentBlock?.typeOptions,
    selectedVariants,
  ])

  return filtered
}

export const useVariantSets = ({
  block,
  treeManager,
}: {
  block: Block
  treeManager: TreeManager<Block, NonNullable<unknown>> | undefined | null
}) => {
  const setsInUse = useMemo(
    () =>
      !treeManager
        ? []
        : getBlockAcceptedVariantSets(block, treeManager.getNode)
            .map((setId) => {
              const block = treeManager.getNode(setId)?.value
              if (block && isVariantSetBlock(block)) {
                return {
                  ...block,
                  value: {
                    ...block.value,
                    variants: withUniqueApiNames(block.value.variants),
                  },
                }
              }
            })
            .filter(Boolean) ?? [],
    [block, treeManager]
  )

  return { setsInUse }
}
